import { useState, FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../components/ui/card';

// Password requirements — mirror backend validate_password_strength exactly so
// the form never lets through a password the server will reject.
//
// The special-character rule was dropped Aug 18 2026 (here and in
// core/security.py PasswordRequirements). It was the rule most likely to bounce
// a first submit on a form someone is meeting for the first time, and 8 chars
// with upper, lower and a digit is still a sound floor.
const PASSWORD_RULES: { label: string; test: (p: string) => boolean }[] = [
  { label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { label: 'An uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { label: 'A lowercase letter', test: (p) => /[a-z]/.test(p) },
  { label: 'A number', test: (p) => /\d/.test(p) },
];

export default function Register() {
  // Email + password only, as of Aug 18 2026.
  //
  // The form used to ask for First Name, Last Name, Email, Company, Password and
  // Confirm Password, then enforce five password rules — six fields on the page
  // that decides whether anyone ever sees the product. The backend never needed
  // any of it: RegisterRequest declares first_name/last_name Optional, and
  // `company` does not exist in the backend at all — it was collected, sent, and
  // silently discarded by Pydantic. Benchmark for a two-field form is 70-85%
  // completion against 40-55% for six fields with five rules.
  //
  // Name is still settable in Account, and the verification email falls back to
  // "Hi there," when it is absent (services/email.py).
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [validationError, setValidationError] = useState('');
  const { register, isLoading, error, clearError } = useAuthStore();
  const navigate = useNavigate();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    clearError();
    setValidationError('');

    // Validate password strength — must match the backend rules exactly so the
    // user never gets a surprise 400 after submitting.
    if (PASSWORD_RULES.some((r) => !r.test(password))) {
      setValidationError('Please meet all the password requirements below.');
      return;
    }

    try {
      await register({ email, password });

      // Check if account needs verification
      const pendingEmail = localStorage.getItem('pendingVerificationEmail');
      if (pendingEmail) {
        navigate('/verify-email-pending');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      // Error is already handled by the store
      console.error('Registration failed:', error);
    }
  };

  const displayError = validationError || error;

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f6f4ee] p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex items-center justify-center mb-2">
            <div className="text-4xl">🤖</div>
          </div>
          <CardTitle className="text-2xl text-center">Create an account</CardTitle>
          <CardDescription className="text-center">
            Free forever · no card required
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {displayError && (
              <div className="bg-destructive/15 text-destructive text-sm p-3 rounded-md">
                {displayError}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@yourcompany.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
                autoComplete="email"
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isLoading}
                  autoComplete="new-password"
                  className="pr-16"
                />
                {/* Replaces the old "Confirm Password" field: letting someone see
                    what they typed solves the same problem as typing it twice,
                    without costing a field. */}
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-medium text-muted-foreground hover:text-foreground"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
              <ul className="mt-1 grid grid-cols-1 gap-0.5 text-xs sm:grid-cols-2">
                {PASSWORD_RULES.map((r) => {
                  const ok = password.length > 0 && r.test(password);
                  return (
                    <li
                      key={r.label}
                      className={ok ? 'text-green-600' : 'text-muted-foreground'}
                    >
                      {ok ? '✓' : '○'} {r.label}
                    </li>
                  );
                })}
              </ul>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <Button
              type="submit"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? 'Creating account...' : 'Create Account'}
            </Button>
            <p className="text-xs text-center text-muted-foreground">
              By creating an account, you agree to our{' '}
              <Link to="/terms" className="text-primary hover:underline">
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link to="/privacy" className="text-primary hover:underline">
                Privacy Policy
              </Link>
            </p>
            <p className="text-sm text-center text-muted-foreground">
              Already have an account?{' '}
              <Link to="/login" className="text-primary hover:underline font-medium">
                Sign in
              </Link>
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
